import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, Edit2, Trash2, X, Check, Loader2,
  TrendingUp, Hash
} from 'lucide-react';

export default function ManageGrowthStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStat, setCurrentStat] = useState(null); // null means adding a new stat

  // Form fields
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [suffix, setSuffix] = useState('+');

  // No error state

  const fetchStats = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'stats'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStats(list);
    } catch (err) {
      console.error("Error loading stats:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load statistics from Firestore.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);


  const openEditModal = (stat) => {
    setCurrentStat(stat);
    setLabel(stat.label || '');
    setValue(stat.value || '');
    setSuffix(stat.suffix || '');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!label || !value) {
      Swal.fire({
        title: 'Required Fields Missing',
        text: 'Label and numerical value are required.',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    // Verify numerical value is parseable
    if (isNaN(parseInt(value))) {
      Swal.fire({
        title: 'Invalid Input',
        text: 'Value must be a valid number (e.g. 450, 25).',
        icon: 'warning',
        confirmButtonColor: '#111111'
      });
      return;
    }

    const statData = {
      label: label.trim(),
      value: String(parseInt(value)), // store as string matching existing schema, parsed in Counter
      suffix: suffix.trim(),
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (currentStat) {
        const docRef = doc(db, 'stats', currentStat.id);
        await updateDoc(docRef, statData);
        Swal.fire({
          title: 'Success',
          text: 'Growth stat successfully updated!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      } else {
        statData.createdAt = new Date();
        await addDoc(collection(db, 'stats'), statData);
        Swal.fire({
          title: 'Success',
          text: 'Growth stat successfully created!',
          icon: 'success',
          confirmButtonColor: '#111111'
        });
      }

      await fetchStats();
      setTimeout(() => {
        setModalOpen(false);
      }, 1000);
    } catch (err) {
      console.error("Error saving growth stat:", err);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred while saving the growth stat.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'stats', id));
      setStats(stats.filter(s => s.id !== id));
      Swal.fire({
        title: 'Success',
        text: 'Growth stat successfully deleted!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting growth stat:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete growth stat.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 font-poppins">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Manage Growth Stats</h1>
          <p className="text-xs text-gray-500">Update key performance indicators displayed in the homepage counter bar.</p>
        </div>
      </div>



      {/* Grid of stats */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
          <Loader2 className="animate-spin text-brand-blue" size={36} />
          <p className="text-xs font-bold text-gray-500">Loading growth stats...</p>
        </div>
      ) : stats.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
          <BarChart3 size={48} className="mx-auto text-gray-300" />
          <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Stats Found</h3>
          <p className="text-xs font-light text-gray-500">There are no growth stats inside the database.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <motion.div
              key={stat.id}
              layout
              className="bg-white p-6 rounded-2xl border border-brand-silver/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-all group"
            >
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div className="w-10 h-10 rounded-xl bg-brand-light text-brand-blue flex items-center justify-center border border-brand-silver/40">
                    <TrendingUp size={20} />
                  </div>
                  {/* Actions */}
                  <div className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openEditModal(stat)}
                      className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-brand-blue hover:bg-brand-blue/5 transition-colors"
                      title="Edit Stat"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(stat.id)}
                      className="p-1.5 rounded-lg border border-brand-silver text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete Stat"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="text-3xl font-extrabold font-montserrat text-brand-navy">
                    {stat.value}
                    <span className="text-brand-blue font-bold">{stat.suffix}</span>
                  </h3>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
                    {stat.label}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* CRUD Overlay Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => !saving && setModalOpen(false)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col"
            >
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">
                    {currentStat ? 'Edit Growth Indicator' : 'Create Growth Indicator'}
                  </h2>
                  <p className="text-xs text-gray-500">Configure public dashboard performance metric.</p>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">


                {/* Metric Label */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Metric Label</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Hospitals Served"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Numerical Value */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Numeric Value</label>
                    <div className="relative">
                      <input
                        type="number"
                        required
                        placeholder="e.g. 450"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                      />
                      <Hash size={14} className="absolute left-3.5 top-3.5 text-gray-400" />
                    </div>
                  </div>

                  {/* Suffix character */}
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 font-poppins">Metric Suffix</label>
                    <input
                      type="text"
                      placeholder="e.g. +, %"
                      value={suffix}
                      onChange={(e) => setSuffix(e.target.value)}
                      className="w-full px-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
                    />
                  </div>
                </div>

                {/* Submit Container */}
                <div className="pt-6 border-t border-brand-silver/50 flex justify-end gap-3">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setModalOpen(false)}
                    className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary py-2.5 px-6 text-xs flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin" size={14} />
                        Saving stat...
                      </>
                    ) : (
                      <>
                        <Check size={14} />
                        {currentStat ? 'Save Changes' : 'Create Stat'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
